import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Star, Phone, MessageCircle, Loader2, Search } from "lucide-react";
import { searchPlaces } from "../lib/api";
import { SERVICE_BY_ID } from "../lib/services";
import { getSession, clearSession } from "../lib/auth";
import { toast } from "sonner";
import ThemeToggle from "../components/ThemeToggle";

function getStoredLocation() {
  try {
    return JSON.parse(sessionStorage.getItem("gp_loc") || "null");
  } catch {
    return null;
  }
}

export default function Results() {
  const { service } = useParams();
  const navigate = useNavigate();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(getStoredLocation());
  const [error, setError] = useState(null);
  const svc = SERVICE_BY_ID[service];
  const ServiceIcon = svc?.icon;

  useEffect(() => {
    if (!getSession()) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const ensureLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      const stored = getStoredLocation();
      if (stored) {
        setLocation(stored);
        return resolve(stored);
      }
      if (!("geolocation" in navigator)) return reject(new Error("Geolocalização indisponível"));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          sessionStorage.setItem("gp_loc", JSON.stringify(loc));
          setLocation(loc);
          resolve(loc);
        },
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loc = await ensureLocation();
      const session = getSession();
      const document = session?.document;
      const data = await searchPlaces({ lat: loc.lat, lng: loc.lng, service, document });
      const allPlaces = data.places || [];
      const filtered = allPlaces.filter((p) => p.distance_m <= 30000);
      filtered.sort((a, b) => a.distance_m - b.distance_m);
      setPlaces(filtered);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || "Falha ao buscar serviços.";
      setError(msg);
      toast.error(msg);
      if (status === 403) {
        clearSession();
        navigate("/", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [ensureLocation, service, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="app-shell pb-10" data-testid="results-screen">
      <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="back-button"
          onClick={() => navigate("/home")}
          className="h-10 w-10 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center"
        >
          <ArrowLeft size={18} className="text-slate-700 dark:text-slate-200" />
        </button>
        <div className="flex items-center gap-2 flex-1">
          {ServiceIcon && (
            <div className="h-8 w-8 rounded-lg bg-[#00D285]/10 flex items-center justify-center">
              <ServiceIcon size={18} className="text-[#00D285]" />
            </div>
          )}
          <div>
            <div className="font-display font-bold text-slate-900 dark:text-white text-lg leading-none">
              {svc?.label || "Serviços"}
            </div>
            <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">próximos a você</div>
          </div>
        </div>
        <ThemeToggle />
      </header>

      {/* Location row */}
      <div className="px-5 pt-4">
        <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-3 py-2 flex items-center gap-2 shadow-sm">
          <div className="h-8 w-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <MapPin size={14} className="text-slate-500 dark:text-slate-400" />
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 truncate flex-1">
            {sessionStorage.getItem("gp_city") || (location
              ? "Localização atual"
              : "Aguardando localização...")}
          </div>
        </div>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-2 px-1">
          Priorizamos prestadores <span className="font-semibold text-[#00B874]">abertos agora</span>,
          serviços <span className="font-semibold text-blue-600 dark:text-blue-400">móveis</span> que vão até você e
          atendimento <span className="font-semibold text-[#00B874]">24h</span>.
        </p>
      </div>

      <main className="px-5 pt-4 pb-10">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-500" data-testid="results-loading">
            <Loader2 size={32} className="animate-spin text-[#00D285]" />
            <p className="mt-3 text-sm">Buscando prestadores próximos...</p>
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-300 rounded-xl p-4 text-sm" data-testid="results-error">
            {error}
            <button
              onClick={fetchData}
              className="block mt-3 text-red-700 dark:text-red-300 font-semibold underline"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!loading && !error && places.length === 0 && (
          <div className="text-center py-16 px-6 text-slate-500 dark:text-slate-400" data-testid="results-empty">
            <Search size={32} className="mx-auto text-slate-300 dark:text-slate-600" />
            <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-200">
              Não encontramos prestadores próximos da localização atual.
            </p>
          </div>
        )}

        <ul className="space-y-3 stagger">
          {places.map((p) => (
            <li key={p.place_id} className="fade-in">
              <button
                data-testid={`place-card-${p.place_id}`}
                onClick={() => navigate(`/prestador/${encodeURIComponent(p.place_id)}?service=${service}`)}
                className="btn-press w-full text-left bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 hover:border-[#00D285]/60 hover:shadow-md rounded-2xl p-4 transition-all"
              >
                {/* Row 1: icon + provider name (wraps to up to 2 lines) */}
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-slate-900 dark:bg-slate-800 text-white flex items-center justify-center shrink-0">
                    {ServiceIcon ? <ServiceIcon size={20} className="text-[#00D285]" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 dark:text-white leading-snug break-words">
                      {p.name}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className="h-8 w-8 rounded-full bg-[#00D285]/10 flex items-center justify-center">
                      <Phone size={14} className="text-[#00D285]" />
                    </div>
                    <div className="h-8 w-8 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                      <MessageCircle size={14} className="text-[#25D366]" />
                    </div>
                  </div>
                </div>

                {/* Row 2: tags + metadata, full width */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2.5 text-xs text-slate-500 dark:text-slate-400">
                  {p.tags && p.tags.map((t) => (
                    <span
                      key={t}
                      className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        t === "24h"
                          ? "bg-[#00D285]/15 text-[#00B874]"
                          : t === "Móvel"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300"
                          : "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                  {p.rating != null && (
                    <span className="inline-flex items-center gap-1 text-amber-500 font-semibold">
                      <Star size={12} fill="#f59e0b" strokeWidth={0} /> {p.rating.toFixed(1)}
                    </span>
                  )}
                  <span>{(p.distance_m / 1000).toFixed(1)} km</span>
                  {p.open_now === true && (
                    <span className="text-[#00B874] font-semibold">Aberto</span>
                  )}
                  {p.open_now === false && (
                    <span className="text-slate-400 dark:text-slate-500">Fechado</span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}
